<script setup lang="ts">
import { onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { Loader2 } from 'lucide-vue-next'
import { useAuthStore } from '@/stores/auth'
import { useRecording } from '@/composables/useRecording'
import { useInAppBrowser } from '@/composables/useInAppBrowser'
import InAppBrowserSheet from '@/components/meet/shared/InAppBrowserSheet.vue'

// meet.kei.bio has no global kei.bio navigation shell of its own (spec §17) —
// each view owns its own header. This root only bootstraps the shared session.
const auth = useAuthStore()
const { t } = useI18n()
const recording = useRecording()
const inAppBrowser = useInAppBrowser()

onMounted(() => {
  void auth.fetchSession()
})

/**
 * 2026-09-14 사용자 지시 — "종료 누르면 회의는 바로 종료되고, 그때 R2 업로드를 시작하고
 * 유튜브처럼 현재 창을 유지하게 안내". MeetRoom.vue는 종료 직후 언마운트되므로, 이 안내와
 * 탭-닫기 가드는 항상 떠 있는 App.vue(root)에 둬야 페이지 이동 후에도 계속 보인다 —
 * useRecording.ts의 상태(uploadInProgress 등)는 모듈 레벨이라 어디서 불러도 같은 값을 본다.
 */
function handleBeforeUnload(e: BeforeUnloadEvent) {
  if (recording.isUploadInProgress()) {
    e.preventDefault()
    e.returnValue = ''
  }
}
onMounted(() => window.addEventListener('beforeunload', handleBeforeUnload))
onUnmounted(() => window.removeEventListener('beforeunload', handleBeforeUnload))

// "38% · 124MB / 326MB" 형태 — 사용자 지시.
function formatMb(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toFixed(0)}MB`
}
function retryRecordingUpload() {
  void recording.retryUpload()
}
</script>

<template>
  <router-view />

  <InAppBrowserSheet
    v-if="inAppBrowser.activeUrl.value"
    :url="inAppBrowser.activeUrl.value"
    :title="inAppBrowser.activeTitle.value"
    @close="inAppBrowser.close"
  />

  <Transition name="upload-banner">
    <div v-if="recording.uploadInProgress.value" class="upload-banner">
      <Loader2 :size="18" class="upload-banner__spinner" />
      <span>
        {{ t('room.savingRecordingBeforeLeave') }}
        <template v-if="recording.totalBytes.value > 0">
          <br />
          {{ t('room.recordingUploadProgressDetail', { percent: recording.uploadProgress.value, uploaded: formatMb(recording.uploadedBytes.value), total: formatMb(recording.totalBytes.value) }) }}
        </template>
      </span>
    </div>
  </Transition>

  <Transition name="upload-banner">
    <div v-if="recording.uploadFailed.value && recording.hasRetryableUpload()" class="upload-banner upload-banner--failed">
      <span>
        <strong>{{ t('room.recordingUploadFailedTitle') }}</strong>
        <br />
        {{ t('room.recordingUploadFailedDesc') }}
      </span>
      <button type="button" class="upload-banner__retry" @click="retryRecordingUpload">{{ t('room.recordingUploadRetry') }}</button>
    </div>
  </Transition>
</template>

<style scoped>
.upload-banner {
  position: fixed;
  left: 50%;
  bottom: calc(var(--space-4) + env(safe-area-inset-bottom));
  transform: translateX(-50%);
  z-index: 9999;
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-3) var(--space-4);
  border-radius: var(--radius-pill);
  background: rgba(20, 20, 22, 0.92);
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
  max-width: calc(100vw - var(--space-8));
  text-align: center;
}

.upload-banner__spinner {
  animation: upload-banner-spin 1s linear infinite;
  flex-shrink: 0;
}

.upload-banner--failed {
  background: rgba(220, 38, 38, 0.95);
}

.upload-banner__retry {
  flex-shrink: 0;
  height: 32px;
  padding: 0 var(--space-3);
  border-radius: var(--radius-pill);
  background: #fff;
  color: #dc2626;
  font-weight: 700;
  font-size: 12px;
}

@keyframes upload-banner-spin {
  to {
    transform: rotate(360deg);
  }
}

.upload-banner-enter-active,
.upload-banner-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.upload-banner-enter-from,
.upload-banner-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(8px);
}
</style>
